import {
  RegisterFormTypes,
  RegisterPageProps,
  useTranslate,
} from '@refinedev/core'
import {
  CardProps,
  Form,
  FormProps,
  FormRule,
  Input,
  LayoutProps,
  Select,
  Typography,
} from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ReactComponent as CheckSelectIcon } from '../../../../common/images/svg/CheckSelect.svg'
// import { ReactComponent as SmartLogo } from '../../../../common/images/svg/SmartIcon.svg'
import { ReactComponent as SmartLogo } from '../../../../common/images/svg/SmartIconHover.svg'
import { ReactComponent as ArrowIcon } from '../../../../common/images/svg/SuffixSelectIcon.svg'
import { CustomButton } from '../../../../components/button/buttonCustom'
import { ButtonTransparent } from '../../../../components/button/buttonTransparent'
import LoadingOverlay from '../../../../components/loading/loadingOverlay'
import ConfirmModal from '../../../../components/modal/confirmModal'
import { companyRegistrationNumberRules } from '../../../../contants/addCompany.constants'
import {
  addressRules,
  clearFieldErrorMessageOnChange,
  companyNameRules,
  onlyLatinCharsDigitsRule,
  onlyLatinCharsDigitsSpecialSymbolsRule,
  trimFormFieldValue,
  websiteRules,
} from '../../../../utils/form.constants'
import useAuth from '../../../../utils/hooks/useAuth'
import { useMetadataCountries } from '../../../../utils/hooks/useMetadataCountries'
import useResponsive from '../../../../utils/hooks/useResponsive'
import { ThemedTitle } from '../../../themedLayout/title'
import { useSharedEmail } from './auth/hooks'
import { ConfirmPasswordForms } from './confirmPasswordForms'
import { buttonStyle, PageTitleStyles } from './styles'

type RegisterProps = RegisterPageProps<LayoutProps, CardProps, FormProps>
type RegisterVariables = {
  email: string
  password: string
  fullName: string
  address: string
  website: string
  regNo: string
  country: string
  confirm: string
}
export const RegisterPage: React.FC<RegisterProps> = ({ formProps, title }) => {
  const [form] = Form.useForm<RegisterVariables & RegisterFormTypes>()
  const { isTablet } = useResponsive()
  const translate = useTranslate()
  const navigate = useNavigate()
  const [searchCountry, setSearchCountry] = useState<any>(undefined)
  const requiredFields = [
    'email',
    'password',
    'confirm',
    'fullName',
    'regNo',
    'address',
    'country',
    'website',
  ]
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [searchParams] = useSearchParams()
  const [email, fullName, address] = useMemo(
    () => [
      searchParams.get('email') ?? undefined,
      decodeURIComponent(searchParams.get('fullName') ?? ''),
      decodeURIComponent(searchParams.get('address') ?? ''),
    ],
    [searchParams]
  )

  const { metadataCountries } = useMetadataCountries()

  const userIdContainsSpaceValidator: FormRule = useCallback(
    () => ({
      validator(_: any, value: string) {
        if (/\s/.test(value)) {
          return Promise.reject(new Error('SMART UID cannot contains spaces'))
        } else {
          return Promise.resolve()
        }
      },
      validateTrigger: 'onBlur',
    }),
    []
  )

  const resetFields = () => {
    form?.resetFields()
    setSearchCountry(undefined)
  }

  const { register, loadingRegister, registerSuccess, setRegisterSuccess } =
    useAuth(resetFields)
  const { saveEmail: shareEmail } = useSharedEmail()

  const handleContinueLogin = () => {
    navigate('/login')
    setRegisterSuccess(false)
    form?.resetFields()
  }

  const onFinish = (values: RegisterVariables) => {
    const { confirm, ...rest } = values
    shareEmail(values.email ?? '')
    register({ ...rest })
  }

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo)
  }

  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields as any)
    return requiredFields.every((field) => !!values[field])
  }

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value)
  }

  const PageTitle =
    title === false ? null : (
      <div style={{ ...PageTitleStyles }}>
        {title ?? <ThemedTitle collapsed={false} />}
      </div>
    )

  const CardContent = (
    <Form
      form={form}
      requiredMark={false}
      {...formProps}
      style={{ width: '100%' }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form)
        setIsAllFieldsFilled(checkAllFieldsFilled())
      }}
      initialValues={{ email, address, fullName }}>
      <div className='auth-form'>
        <Form.Item
          label='Email'
          name='email'
          validateTrigger='onBlur'
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            {
              required: true,
              validateTrigger: 'onBlur',
              message: 'This field is required',
            },
            {
              type: 'email',
              message: translate(
                'pages.register.errors.validEmail',
                'Invalid email address'
              ),
              validateTrigger: 'onBlur',
            },
          ]}
          style={{ marginTop: '-20px' }}>
          <Input
            readOnly
            onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
            size='large'
            placeholder={translate(
              'pages.register.fields.email',
              'Enter email address'
            )}
            style={{ ...buttonStyle }}
          />
        </Form.Item>
        <ConfirmPasswordForms />
        <Form.Item
          style={{
            borderTop: '1px solid rgba(255,255,255, 0.15)',
            paddingTop: '24px',
          }}
          label='Full Name/ Company Name'
          name='fullName'
          validateTrigger='onBlur'
          rules={[...companyNameRules]}>
          <Input
            placeholder='Enter full legal name of individual or company'
            style={{ ...buttonStyle }}
            size='large'
          />
        </Form.Item>
        <Form.Item
          label='Reg. No'
          name='regNo'
          validateTrigger='onBlur'
          rules={companyRegistrationNumberRules}>
          <Input
            placeholder='Enter registration number'
            style={{ ...buttonStyle }}
            size='large'
          />
        </Form.Item>
        <Form.Item
          label='Country of incorporation'
          name='country'
          validateTrigger='onBlur'
          rules={[
            {
              required: true,
              message: 'This field is required',
              validateTrigger: 'onBlur',
            },
          ]}>
          <Select
            showSearch
            placeholder='Select country of registration'
            suffixIcon={<ArrowIcon />}
            className='select-auth'
            onChange={handleChangeCountry}
            optionLabelProp='label'
            value={searchCountry}
            filterOption={(input, option) => {
              const label = option?.label
              if (typeof label === 'string') {
                return label.toLowerCase().includes(input.toLowerCase())
              }
              return false
            }}>
            {metadataCountries.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}>
                    {item.label}
                  </span>
                  {searchCountry === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Company's Address"
          name='address'
          rules={addressRules}
          validateTrigger='onBlur'>
          <Input
            placeholder='Enter company’s registered address'
            style={{ ...buttonStyle }}
            size='large'
          />
        </Form.Item>
        <Form.Item
          label='Website'
          name='website'
          validateTrigger='onBlur'
          rules={websiteRules}>
          <Input
            size='large'
            placeholder={translate(
              'pages.register.fields.website',
              'Enter company website URL'
            )}
            onBlur={trimFormFieldValue('website', form)}
            style={{ ...buttonStyle }}
          />
        </Form.Item>
        <Typography
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: 'rgba(255, 255, 255, 0.8)',
          }}>
          Note: Your company details will be checked against sanctions lists.
          Please ensure that all company information provided above is correct
          before submitting this form.
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <CustomButton
            width={'100%'}
            type='submit'
            size={isTablet ? 'medium' : 'large'}
            text={translate('pages.register.buttons.submit', 'Register')}
            disabled={!isAllFieldsFilled || !searchCountry}
          />
          <ButtonTransparent
            width={'100%'}
            size={isTablet ? 'medium' : 'large'}
            text={'Back'}
            onClick={() => {
              if (loadingRegister) return
              // navigate(-1)
              navigate('/login')
            }}
            textClassName='text-button-back'
          />
        </div>
      </div>
    </Form>
  )

  return (
    <div className='auth-register-box'>
      <div className='auth-render' style={{ marginTop: '50px' }}>
        {PageTitle}
        {CardContent}
      </div>
      <SmartLogo className='auth-logo' style={{ width: 56, height: 46 }} />
      <LoadingOverlay loading={loadingRegister} time={0} />
      <ConfirmModal
        type='success'
        title='Signup Successful'
        description='A verification email has been sent to your email. Please check your email to verify your account before logging in.'
        open={registerSuccess}
        showButtonCancel={false}
        onCancel={() => null}
        onSubmit={handleContinueLogin}
        textSubmit='OK'
      />
    </div>
  )
}
